import chevronDoubleLeft from '@/assets/icons/icon-chevron-double-left.svg';
import chevronLeft from '@/assets/icons/icon-chevron-left.svg';
import chevronRight from '@/assets/icons/icon-chevron-right.svg';
import chevronDoubleRight from '@/assets/icons/icon-chevron-double-right.svg';
import './Pagination.scss';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  basePath: string;
}

function getPageUrl(basePath: string, page: number): string {
  if (page === 1) return basePath;
  const base = basePath.endsWith('/') ? basePath : `${basePath}/`;
  return `${base}page/${page}/`;
}

function getVisiblePages(current: number, total: number): number[] {
  if (total <= 9) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  let start: number;
  if (current <= 5) {
    start = 1;
  } else if (current >= total - 4) {
    start = total - 8;
  } else {
    start = current - 4;
  }

  return Array.from({ length: 9 }, (_, i) => start + i);
}

function getJumperLinks(total: number): number[][] {
  const groups: number[][] = [];
  let step = 10;
  while (step <= total) {
    const group: number[] = [];
    for (let p = step; p <= step * 9 && p <= total; p += step) {
      group.push(p);
    }
    if (group.length > 0) groups.push(group);
    step *= 10;
  }
  return groups;
}

export function Pagination({ currentPage, totalPages, basePath }: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages = getVisiblePages(currentPage, totalPages);
  const jumperGroups = totalPages > 9 ? getJumperLinks(totalPages) : [];

  return (
    <nav className="pagination" aria-label="Pagination">
      <div className="pagination__main">
        {/* Mobile only: prev/next + first/last as labeled rows */}
        <ul className="pagination__nav pagination__nav--mobile">
          <li>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- on the first page the href is dropped on
                purpose so the control is inert; turning it into a button would change the whole pagination markup. */}
            <a
              href={currentPage > 1 ? getPageUrl(basePath, currentPage - 1) : undefined}
              className={`pagination__btn${currentPage === 1 ? ' pagination__btn--disabled' : ''}`}
              aria-label="Page précédente"
            >
              <img src={chevronLeft} alt="" width={16} height={16} />
              <span>Précédent</span>
            </a>
          </li>
          <li>
            <a
              href={currentPage < totalPages ? getPageUrl(basePath, currentPage + 1) : undefined}
              className={`pagination__btn${currentPage === totalPages ? ' pagination__btn--disabled' : ''}`}
              aria-label="Page suivante"
            >
              <span>Suivant</span>
              <img src={chevronRight} alt="" width={16} height={16} />
            </a>
          </li>
        </ul>

        <ul className="pagination__nav pagination__nav--mobile">
          <li>
            <a
              href={getPageUrl(basePath, 1)}
              className={`pagination__btn${currentPage === 1 ? ' pagination__btn--disabled' : ''}`}
              aria-label="Première page"
            >
              <img src={chevronDoubleLeft} alt="" width={16} height={16} />
              <span>Début</span>
            </a>
          </li>
          <li>
            <a
              href={getPageUrl(basePath, totalPages)}
              className={`pagination__btn${currentPage === totalPages ? ' pagination__btn--disabled' : ''}`}
              aria-label="Dernière page"
            >
              <span>Fin</span>
              <img src={chevronDoubleRight} alt="" width={16} height={16} />
            </a>
          </li>
        </ul>

        {/* Desktop only: « ‹ [pages] › » inline */}
        <ul className="pagination__inline">
          <li>
            <a
              href={getPageUrl(basePath, 1)}
              className={`pagination__btn${currentPage === 1 ? ' pagination__btn--disabled' : ''}`}
              aria-label="Première page"
            >
              <img src={chevronDoubleLeft} alt="" width={16} height={16} />
            </a>
          </li>
          <li>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- on the first page the href is dropped on
                purpose so the control is inert; turning it into a button would change the whole pagination markup. */}
            <a
              href={currentPage > 1 ? getPageUrl(basePath, currentPage - 1) : undefined}
              className={`pagination__btn${currentPage === 1 ? ' pagination__btn--disabled' : ''}`}
              aria-label="Page précédente"
            >
              <img src={chevronLeft} alt="" width={16} height={16} />
            </a>
          </li>
        </ul>

        <ul className="pagination__pages">
          {pages.map((page) => (
            <li key={page}>
              <a
                href={getPageUrl(basePath, page)}
                className={`pagination__page${page === currentPage ? ' pagination__page--active' : ''}`}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </a>
            </li>
          ))}
        </ul>

        <ul className="pagination__inline">
          <li>
            <a
              href={currentPage < totalPages ? getPageUrl(basePath, currentPage + 1) : undefined}
              className={`pagination__btn${currentPage === totalPages ? ' pagination__btn--disabled' : ''}`}
              aria-label="Page suivante"
            >
              <img src={chevronRight} alt="" width={16} height={16} />
            </a>
          </li>
          <li>
            <a
              href={getPageUrl(basePath, totalPages)}
              className={`pagination__btn${currentPage === totalPages ? ' pagination__btn--disabled' : ''}`}
              aria-label="Dernière page"
            >
              <img src={chevronDoubleRight} alt="" width={16} height={16} />
            </a>
          </li>
        </ul>
      </div>

      <div className="pagination__separator"></div>

      {jumperGroups.length > 0 && (
        <div className="pagination__jumper">
          {jumperGroups.map((group, i) => (
            <ul key={i} className="pagination__jumper-group">
              {group.map((page) => (
                <li key={page}>
                  <a href={getPageUrl(basePath, page)}>{page}</a>
                </li>
              ))}
            </ul>
          ))}
        </div>
      )}
    </nav>
  );
}
